<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestFieldGrid from '@/components/finest-field-grid/FinestFieldGrid.vue'
import type {
  InlineEditableCommitPayload,
  InlineEditableField,
  InlineEditableModel,
  InlineEditableValue,
} from '@/components/finest-field-grid/types'
import { useEnumOptions } from '@/composables/useEnumOptions'
import { useSyncedModel } from '@/composables/useSyncedModel'
import { useEstateObjectUpdate } from '@/pages/investors/portfolio/useEstateObjectUpdate'
import type { ExtendedEnum } from '@/types'
import type { FieldStateMap } from '@/utils/field-state'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import ElevatorType = App.Enums.EstateObject.ElevatorType
import EnergyCertificateType = App.Enums.EstateObject.EnergyCertificateType
import EnergySourceType = App.Enums.EstateObject.EnergySourceType
import HeatingType = App.Enums.EstateObject.HeatingType
import RoofType = App.Enums.EstateObject.RoofType
import WindowType = App.Enums.EstateObject.WindowType

const props = defineProps<{
  fieldStates: FieldStateMap
  estateObject: EstateObjectData
  roofTypes: ExtendedEnum<RoofType>[]
  windowTypes: ExtendedEnum<WindowType>[]
  elevatorTypes: ExtendedEnum<ElevatorType>[]
  heatingTypes: ExtendedEnum<HeatingType>[]
  energySourceTypes: ExtendedEnum<EnergySourceType>[]
  energyCertificateTypes: ExtendedEnum<EnergyCertificateType>[]
}>()

const { t } = useI18n()
const toEnumOptions = useEnumOptions()

const buildingFields = computed<InlineEditableField[]>(() => [
  { key: 'number_of_floors', label: t('fields.number_of_floors'), type: 'number' },
  { key: 'number_of_units', label: t('fields.number_of_units'), type: 'number' },
  { key: 'number_of_parking_spaces', label: t('fields.number_of_parking_spaces'), type: 'number' },
  { key: 'number_of_garages', label: t('fields.number_of_garages'), type: 'number' },
  {
    key: 'roof_type',
    label: t('fields.roof_type'),
    type: 'select',
    options: toEnumOptions(props.roofTypes, 'enums.roof_type'),
  },
  {
    key: 'window_type',
    label: t('fields.window_type'),
    type: 'select',
    options: toEnumOptions(props.windowTypes, 'enums.window_type'),
  },
  {
    key: 'elevator_type',
    label: t('fields.elevator_type'),
    type: 'select',
    fullWidth: true,
    options: toEnumOptions(props.elevatorTypes, 'enums.elevator_type'),
  },
])

const energyFields = computed<InlineEditableField[]>(() => [
  {
    key: 'heating_type',
    label: t('fields.heating_type'),
    type: 'select',
    options: toEnumOptions(props.heatingTypes, 'enums.heating_type'),
  },
  {
    key: 'energy_source_type',
    label: t('fields.energy_source_type'),
    type: 'select',
    options: toEnumOptions(props.energySourceTypes, 'enums.energy_source_type'),
  },
  { key: 'has_energy_certificate', label: t('fields.has_energy_certificate'), type: 'boolean' },
  {
    key: 'energy_certificate_type',
    label: t('fields.energy_class'),
    type: 'select',
    options: toEnumOptions(props.energyCertificateTypes, 'enums.energyCertificateType'),
    disabled: !props.estateObject.has_energy_certificate,
  },
])

const editableKeys = computed(() => [...buildingFields.value, ...energyFields.value].map((field) => field.key))

const seedModel = (): InlineEditableModel => {
  const source = props.estateObject as unknown as Record<string, InlineEditableValue>

  return Object.fromEntries(editableKeys.value.map((key) => [key, source[key] ?? null]))
}

const { model, confirm } = useSyncedModel(seedModel)

const update = useEstateObjectUpdate(props.estateObject.id ?? '', confirm)

const save = ({ key, value }: InlineEditableCommitPayload) => update({ [key]: value })
</script>

<template>
  <div class="grid items-start gap-4 lg:grid-cols-2">
    <FinestCard :title="t('cards.building')" icon="pi pi-building">
      <FinestFieldGrid v-model="model" :fields="buildingFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>

    <FinestCard :title="t('cards.energy')" icon="pi pi-bolt">
      <FinestFieldGrid v-model="model" :fields="energyFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "cards": {
      "building": "Gebäude",
      "energy": "Technik & Energie"
    },
    "fields": {
      "number_of_floors": "Anzahl Geschosse",
      "number_of_units": "Anzahl der Einheiten im Objekt",
      "number_of_parking_spaces": "Stellplätze",
      "number_of_garages": "Garagen",
      "roof_type": "Dachform",
      "window_type": "Fenster",
      "elevator_type": "Aufzug",
      "heating_type": "Heizungsart",
      "energy_source_type": "Energieträger",
      "has_energy_certificate": "Energieausweis vorhanden",
      "energy_class": "Energieklasse"
    }
  },
  "en": {
    "cards": {
      "building": "Building",
      "energy": "Systems & energy"
    },
    "fields": {
      "number_of_floors": "Number of floors",
      "number_of_units": "Number of units in the property",
      "number_of_parking_spaces": "Parking spaces",
      "number_of_garages": "Garages",
      "roof_type": "Roof shape",
      "window_type": "Windows",
      "elevator_type": "Elevator",
      "heating_type": "Heating type",
      "energy_source_type": "Energy source",
      "has_energy_certificate": "Energy certificate available",
      "energy_class": "Energy class"
    }
  }
}
</i18n>
